import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getDashboardCheckedInSelector, getDashboardCheckedIn} from '@/store/organization-dashboard';
//mui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
//constants
import {
  BIDDER_ID,
  CHECKED_IN_DASH,
  DOWNLOAD_CSV,
  EMAIL,
  FIRST_NAME_CAMEL,
  HASH,
  ID,
  NAME,
  PHONE_NUMBER,
  SPECIAL_MEALS,
  STATUS,
  defaultPage,
  defaultPageSize,
} from '@/utils/constants';
//helpers
import {downloadFile, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as ImportFileIcon} from '@/assets/icons/import-file.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
//services
import {OrganizationDashboardService} from '@/utils/services';
//enums
import {STATISTIC, TABLES_ENTITIES} from '@/utils/enums';
//components
import Loader from '@/shared/components/Loader';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
//styles
import {statisticCheckedInStyle} from './StatisticCheckedIn.styles';

const StatisticCheckedIn = () => {
  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [searchName, setSearchName] = useState('');

  const [sortColumn, setSortColumn] = useState<string>();
  const [isOrderDesc, setIsOrderDesc] = useState<boolean>();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const checkedIn = useAppSelector(getDashboardCheckedInSelector);

  const {data, pageSize, totalCount} = checkedIn;

  useEffect(() => {
    if (currentEvent) getCheckedIn(searchName);
  }, [currentEvent, currentPage, sortColumn, isOrderDesc]);

  const handleSortChange = (newSortColumn: string) => () => {
    const isAsc = sortColumn !== newSortColumn || !isOrderDesc;
    setIsOrderDesc(isAsc);
    setSortColumn(newSortColumn);
  };

  const getCheckedIn = async (value?: string) => {
    setIsDataLoaded(false);

    try {
      const reqBody = {
        eventId: currentEvent!.id,
        page: currentPage,
        sortColumn,
        isOrderDesc,
        searchName: value,
      };

      await dispatch(getDashboardCheckedIn(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const handleDownloadSvc = async () => {
    if (currentEvent) {
      const file = await OrganizationDashboardService.getDashboardCheckedInCsv(currentEvent.id);
      downloadFile(file.data, STATISTIC.CHECKED_IN);
    }
  };

  const handleSearchChange = (value: string) => {
    setCurrentPage(1);
    setSearchName(value);
  };

  const handlePaginationChange = (step: number) => {
    if (isOrderDesc) setIsOrderDesc(false);
    if (sortColumn?.length) setSortColumn('');

    setCurrentPage(step);
  };

  const handlePressEnter = () => {
    setCurrentPage(defaultPage);
  };

  const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

  useEffect(() => {
    if (currentEvent) getCheckedIn(searchName);
  }, [currentEvent, currentPage, sortColumn, isOrderDesc, searchName]);

  return (
    <Box sx={statisticCheckedInStyle.wrapper}>
      <SearchHeader
        page={currentPage}
        items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
        total={totalCount}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        unitName={TABLES_ENTITIES.GUESTS}
        actionButton={
          <Button
            onClick={handleDownloadSvc}
            title={DOWNLOAD_CSV}
            disabled={!totalCount}
            startIcon={<ImportFileIcon />}
            sx={statisticCheckedInStyle.searchButton}
          />
        }
      />

      {isDataLoaded ? (
        totalCount ? (
          <Box sx={statisticCheckedInStyle.tableContent}>
            <TableContainer sx={statisticCheckedInStyle.container}>
              <Table sx={statisticCheckedInStyle.table}>
                <TableHead>
                  <TableRow sx={statisticCheckedInStyle.tableHeader}>
                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(ID)} IconComponent={UpDownIcon}>
                        {BIDDER_ID}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(FIRST_NAME_CAMEL)} IconComponent={UpDownIcon}>
                        {NAME}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{EMAIL}</TableCell>

                    <TableCell>{PHONE_NUMBER}</TableCell>

                    <TableCell>{SPECIAL_MEALS}</TableCell>

                    <TableCell>{STATUS}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.id} sx={statisticCheckedInStyle.tableRow}>
                      <TableCell>
                        {HASH}
                        {row.attendeeId}
                      </TableCell>

                      <TableCell>{`${row.firstName} ${row.lastName}`}</TableCell>

                      <TableCell>{row.email}</TableCell>

                      <TableCell>{row.phoneNumber}</TableCell>

                      <TableCell>{row.meals.join(', ') || '-'}</TableCell>

                      <TableCell>
                        <Typography sx={{...statisticCheckedInStyle.status, ...getPayStatusSx(row.status)}}>
                          {row.status.replace(/\bCheckedIn\b/g, CHECKED_IN_DASH)}
                          <EllipseIcon />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={statisticCheckedInStyle.pagination}>
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(step) => handlePaginationChange(step)}
              />
            </Box>
          </Box>
        ) : (
          <EmptyTableData />
        )
      ) : (
        <Loader parentClassName={statisticCheckedInStyle.loader} size={50} />
      )}
    </Box>
  );
};

export default StatisticCheckedIn;
